import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, X, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { compressImage } from "../lib/image";
import { createCatalogItem, updateCatalogItem, suggestCatalogDescription } from "../lib/api";
import { usePlan } from "../context/PlanContext";

const inputCls = "w-full h-10 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-slate-900 px-3 text-[13.5px] text-slate-900 placeholder:text-slate-400 outline-none transition-shadow";
const EMPTY = { name: "", kind: "service", description: "", price: "", unit: "", category: "", synonyms: "", image: null };

const toForm = (it) => it ? { name: it.name || "", kind: it.kind || "service", description: it.description || "", price: it.price == null ? "" : String(it.price).replace(".", ","), unit: it.unit || "", category: it.category || "", synonyms: (it.synonyms || []).join(", "), image: it.image || null } : EMPTY;

export default function CatalogItemDialog({ open, onClose, companyId, item, categories = [], onSaved }) {
  const { openUpgrade } = usePlan();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (open) setF(toForm(item)); }, [open, item]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const suggest = async () => {
    if (!f.name.trim() || aiBusy) return;
    setAiBusy(true);
    try { const r = await suggestCatalogDescription(companyId, { name: f.name.trim(), kind: f.kind, category: f.category.trim() || null }); set("description", r.description || ""); }
    catch { toast.error("Não foi possível gerar a descrição agora"); }
    finally { setAiBusy(false); }
  };

  const pick = async (file) => {
    if (!file) return;
    try { set("image", await compressImage(file, 800)); } catch { toast.error("Não foi possível ler a imagem"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || busy) return;
    const priceNum = f.price.trim() === "" ? null : Number(String(f.price).replace(/\./g, "").replace(",", "."));
    if (priceNum !== null && (Number.isNaN(priceNum) || priceNum < 0)) return toast.error("Preço inválido");
    const payload = {
      name: f.name.trim(), kind: f.kind, description: f.description.trim() || null, price: priceNum, unit: f.unit.trim() || null,
      category: f.category.trim() || null, synonyms: f.synonyms.split(",").map((s) => s.trim()).filter(Boolean), image: f.image || null,
    };
    setBusy(true);
    try {
      const saved = item?.id ? await updateCatalogItem(companyId, item.id, payload) : await createCatalogItem(companyId, payload);
      toast.success(item?.id ? "Item atualizado" : "Item adicionado ao catálogo");
      onSaved(saved);
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "catalog_limit") { onClose(); openUpgrade("catalog"); }
      else toast.error(d?.message || (typeof d === "string" ? d : null) || "Não foi possível salvar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-3xl p-0 overflow-hidden" data-testid="catalog-item-dialog">
        <form onSubmit={submit}>
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="text-[17px]">{item?.id ? "Editar item" : "Novo item do catálogo"}</DialogTitle>
            <DialogDescription className="text-[12.5px]">Só o nome é obrigatório. Preço, descrição, foto e categoria são opcionais — a IA usa o que estiver preenchido.</DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-2 grid gap-4 max-h-[65vh] overflow-y-auto">
            <div className="flex gap-4">
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="catalog-image-btn"
                className="relative shrink-0 w-24 h-24 rounded-2xl ring-1 ring-dashed ring-slate-300 bg-slate-50 grid place-items-center overflow-hidden hover:bg-slate-100 transition-colors">
                {f.image ? <img src={f.image} alt="" className="w-full h-full object-cover" /> : <span className="text-slate-400 flex flex-col items-center gap-1 text-[10.5px]"><ImagePlus size={18} /> Foto</span>}
                {f.image && <span onClick={(e) => { e.stopPropagation(); set("image", null); }} data-testid="catalog-image-remove" className="absolute top-1 right-1 grid place-items-center w-5 h-5 rounded-full bg-white/90 text-slate-600 shadow"><X size={11} /></span>}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="catalog-image-input" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
              <div className="flex-1 grid gap-3">
                <div className="flex rounded-xl bg-slate-100 p-1 text-[12.5px] font-medium">
                  {[["service", "Serviço"], ["product", "Produto"]].map(([v, l]) => (
                    <button type="button" key={v} onClick={() => set("kind", v)} data-testid={`catalog-kind-${v}`} className={`flex-1 h-8 rounded-lg transition-colors ${f.kind === v ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{l}</button>
                  ))}
                </div>
                <input className={inputCls} placeholder="Nome (ex.: Limpeza completa)" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="catalog-name" autoFocus required maxLength={120} />
              </div>
            </div>
            <div className="relative">
              <textarea className={`${inputCls} h-auto min-h-[76px] py-2.5 pr-28 resize-none`} placeholder="Descrição (opcional) — aparece no orçamento" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="catalog-description" maxLength={1500} />
              <button type="button" disabled={!f.name.trim() || aiBusy} onClick={suggest} data-testid="catalog-ai-description"
                className="absolute right-2 top-2 inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-[#F4F5FF] text-[#4A3BFA] text-[11.5px] font-semibold hover:bg-[#E9EBFF] disabled:opacity-40 transition-colors">
                {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Gerar com IA
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11.5px] font-medium text-slate-500 ml-1">Preço (R$) · opcional</label>
                <input className={inputCls} inputMode="decimal" placeholder="Sob consulta" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="catalog-price" />
              </div>
              <div>
                <label className="text-[11.5px] font-medium text-slate-500 ml-1">Unidade · opcional</label>
                <input className={inputCls} placeholder="un, m², hora, diária…" value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="catalog-unit" maxLength={30} />
              </div>
            </div>
            <div>
              <label className="text-[11.5px] font-medium text-slate-500 ml-1">Categoria · opcional</label>
              <input className={inputCls} list="catalog-categories" placeholder="Ex.: Limpeza, Instalação, Peças" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="catalog-category" maxLength={60} />
              <datalist id="catalog-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className="text-[11.5px] font-medium text-slate-500 ml-1">Outros nomes que você usa · opcional</label>
              <input className={inputCls} placeholder="Ex.: faxina, limpeza pesada (separe por vírgula)" value={f.synonyms} onChange={(e) => set("synonyms", e.target.value)} data-testid="catalog-synonyms" />
              <p className="mt-1 ml-1 text-[11px] text-slate-400">Ajuda a IA a reconhecer o item quando você escreve de outro jeito.</p>
            </div>
          </div>
          <div className="px-6 py-4 flex items-center justify-end gap-2 border-t border-slate-100">
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-full h-9 text-[13px]" data-testid="catalog-cancel">Cancelar</Button>
            <Button type="submit" disabled={busy || !f.name.trim()} className="rounded-full h-9 bg-slate-900 hover:bg-slate-800 text-[13px] gap-1.5" data-testid="catalog-save">
              {busy && <Loader2 size={14} className="animate-spin" />} {item?.id ? "Salvar" : "Adicionar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
